import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, DollarSign, Percent, Calculator } from 'lucide-react';
export function App() {
  // State for editable inputs
  const [loanAmount, setLoanAmount] = useState('300,000');
  const [rateCost, setRateCost] = useState('3.311');
  const [borrowerPoints, setBorrowerPoints] = useState('2.311');
  const [lenderCredit, setLenderCredit] = useState('0.500');
  // Constants (Hidden Logic)
  const loTargetCompBps = 100;
  const builtInMarginPoints = 6;
  // Calculation Logic
  const commission = useMemo(() => {
    const amount = parseFloat(loanAmount.replace(/,/g, '')) || 0;
    const rate = parseFloat(rateCost) || 0;
    const borrower = parseFloat(borrowerPoints) || 0;
    const lender = parseFloat(lenderCredit) || 0;
    // Step 1: Pricing Short / Discount
    const pricingShortPoints = Math.max(rate - borrower, 0);
    // Step 2: Total Deduction From Margin
    const totalDeductionPoints = pricingShortPoints + lender;
    // Step 3: Remaining Margin Points
    const remainingMarginPoints = Math.max(builtInMarginPoints - totalDeductionPoints, 0);
    // Step 4: LO Comp Share of Margin
    const loCompShareOfMargin = loTargetCompBps / 100 / builtInMarginPoints;
    // Step 5: Estimated LO Comp Points
    const estimatedLoCompPoints = remainingMarginPoints * loCompShareOfMargin;
    // Step 6: LO Commission Dollars
    return amount * estimatedLoCompPoints / 100;
  }, [loanAmount, rateCost, borrowerPoints, lenderCredit]);
  // Input Handlers
  const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    const formatted = val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setLoanAmount(formatted);
  };
  const handleDecimalChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimals
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    setter(val);
  };
  // Format currency for display
  const formattedCommission = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(commission);
  return <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-50 text-slate-900" data-id="element-0">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-[700px] bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden" data-id="element-1">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-100" data-id="element-2">
          <div className="flex items-center gap-3 mb-2" data-id="element-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-4">
              <Calculator className="w-5 h-5" data-id="element-5" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-id="element-6">
              LO Compensation Calculator
            </h1>
          </div>
          <p className="text-slate-500 text-sm leading-relaxed" data-id="element-7">
            Estimate your commission based on loan pricing, borrower-paid
            points, and lender credits.
          </p>
        </div>

        <div className="p-8 space-y-8" data-id="element-8">
          {/* Inputs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9">
            {/* Loan Amount */}
            <div className="space-y-2" data-id="element-10">
              <label className="block text-sm font-medium text-slate-700" htmlFor="loanAmount" data-id="element-11">
                Loan Amount
              </label>
              <div className="relative" data-id="element-12">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13">
                  <DollarSign className="h-4 w-4 text-slate-400" data-id="element-14" />
                </div>
                <input type="text" id="loanAmount" value={loanAmount} onChange={handleLoanAmountChange} className="block w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none sm:text-sm" placeholder="0" data-id="element-15" />
              </div>
            </div>

            {/* Rate Cost / Points */}
            <div className="space-y-2" data-id="element-16">
              <label className="block text-sm font-medium text-slate-700" htmlFor="rateCost" data-id="element-17">
                Rate Cost / Points
              </label>
              <div className="relative" data-id="element-18">
                <input type="text" id="rateCost" value={rateCost} onChange={handleDecimalChange(setRateCost)} className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none sm:text-sm" placeholder="0.000" data-id="element-19" />
              </div>
            </div>

            {/* Borrower Charged Points */}
            <div className="space-y-2" data-id="element-20">
              <label className="block text-sm font-medium text-slate-700" htmlFor="borrowerPoints" data-id="element-21">
                Borrower Charged Points
              </label>
              <div className="relative" data-id="element-22">
                <input type="text" id="borrowerPoints" value={borrowerPoints} onChange={handleDecimalChange(setBorrowerPoints)} className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none sm:text-sm" placeholder="0.000" data-id="element-23" />
              </div>
            </div>

            {/* Lender Credit Points */}
            <div className="space-y-2" data-id="element-24">
              <label className="block text-sm font-medium text-slate-700" htmlFor="lenderCredit" data-id="element-25">
                Lender Credit Points
              </label>
              <div className="relative" data-id="element-26">
                <input type="text" id="lenderCredit" value={lenderCredit} onChange={handleDecimalChange(setLenderCredit)} className="block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none sm:text-sm" placeholder="0.000" data-id="element-27" />
              </div>
            </div>
          </div>

          {/* Compensation Settings (Read-Only) */}
          <div className="pt-6 border-t border-slate-100" data-id="element-28">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-29">
              Compensation Settings
            </h3>
            <div className="flex items-center justify-between p-4 bg-slate-50/50 border border-slate-100 rounded-xl" data-id="element-30">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-600" data-id="element-31">
                <Lock className="w-4 h-4 text-slate-400" data-id="element-32" />
                LO Target Comp
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-white px-3 py-1.5 rounded-md border border-slate-200 shadow-sm" data-id="element-33">
                {loTargetCompBps}{' '}
                <span className="text-slate-400 font-normal" data-id="element-34">bps</span>
              </div>
            </div>
          </div>

          {/* Result Area */}
          <motion.div layout className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 text-white shadow-lg shadow-slate-900/10" data-id="element-35">
            {/* Subtle background decoration */}
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" data-id="element-36" />
            <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl pointer-events-none" data-id="element-37" />

            <div className="relative z-10 flex flex-col items-center text-center" data-id="element-38">
              <h2 className="text-slate-300 text-sm font-medium mb-2" data-id="element-39">
                Estimated LO Commission
              </h2>

              <div className="h-[60px] flex items-center justify-center overflow-hidden" data-id="element-40">
                <AnimatePresence mode="popLayout" data-id="element-41">
                  <motion.div key={formattedCommission} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3,
                  ease: 'easeOut'
                }} className="text-4xl sm:text-5xl font-bold tracking-tight text-white" data-id="element-42">
                    {formattedCommission}
                  </motion.div>
                </AnimatePresence>
              </div>

              <p className="text-slate-400 text-xs mt-4 max-w-sm" data-id="element-43">
                Based on current pricing inputs and your compensation plan.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>;
}